/*
  Dr. Motion exit-intent popups (Klaviyo)
  ------------------------------------------------------------------------------
  When a visitor moves to leave the site, this opens at most ONE Klaviyo exit
  popup per visit, chosen by the rules below, and fills in the dynamic parts:
  the visitor's last viewed products (popup 1), the amount left to reach free
  shipping plus a frequently-bought-together product (popup 2), and a
  scrolling strip of best-selling Peanuts product cards (popup 3).

  Frequently-bought-together pairs and the best-seller ranking come from
  drm-fbt.json in Shopify Files (Content > Files), built from order history by
  tools/build_fbt.py. Products without pairs fall back to Shopify's related
  products.

  Each popup has a desktop form and a 358px mobile form: Klaviyo shrinks any
  popup wider than the screen, so phones get their own form instead of a shrunken desktop
  one. Each Klaviyo form must be a popup with Display timing set to "Custom trigger only"
  (Targeting & Behavior), and must be live to open.

  A holdout test (CONFIG.experiment) withholds the popups from a random half of the
  visitors who would get one, to measure what the popups add in sales.

  QA helpers (browser console):
    DRMExitPopups.state()    what the script knows about this visitor
    DRMExitPopups.trigger()  run the exit check now
    DRMExitPopups.reset()    forget everything stored for this visitor
    DRMExitPopups.joinTestGroup('popup' | 'holdout')  put this visitor in a test group
  Add ?drm_exit_debug=1 to any URL to log decisions to the console.
*/
(function () {
  'use strict';

  var CONFIG = {
    // Klaviyo form IDs (end of the form editor URL), per screen size.
    forms: {
      cartUnder50: { desktop: 'VkrhwG', mobile: 'WQm9N8' },     // Cart under the free shipping threshold
      recentlyViewed: { desktop: 'WTMU4N', mobile: 'XikVKq' },  // "Pick up where you left off"
      noPeanuts: { desktop: 'ViiJKe', mobile: 'VyyvNj' }       // Leaving without having seen Peanuts
    },
    // Screens narrower than this get the mobile forms (desktop forms are up to 760px wide).
    mobileBelowWidth: 800,
    // On exit, the first popup in this list whose rules match is the one shown.
    priority: ['cartUnder50', 'recentlyViewed', 'noPeanuts'],

    // Free shipping threshold per cart currency, in cents (matches the site banner).
    freeShippingThresholds: { USD: 5000, CAD: 13500 },
    recentlyViewedCooldownDays: 5,
    noPeanutsCooldownDays: 3,
    noPeanutsMaxShows: 2,
    maxProductsShown: 2,
    peanutsCollection: 'peanuts',
    peanutsStripCount: 8,

    // Exit intent is ignored this soon after a page loads; the popup is prepared at this point.
    minSecondsOnPage: 3,
    exitTopMarginPx: 10,

    // Each form's background or banner image, fetched ahead so the popup appears complete.
    formImages: {
      WTMU4N: 'https://cdn.klaviyomail.com/company/YupLFH/images/0a6a83f3-d2d5-484c-adf0-c7617265172d.jpeg',
      XikVKq: 'https://cdn.klaviyomail.com/company/YupLFH/images/2eb7ead3-ed94-4bfb-8d68-2db5fb72c03a.jpeg',
      VkrhwG: 'https://cdn.klaviyomail.com/company/YupLFH/images/64c963a8-bb5a-430a-8e92-3db60c58f12d.jpeg',
      WQm9N8: 'https://cdn.klaviyomail.com/company/YupLFH/images/c1e773bf-70ac-4e4a-8c2d-d4a1670ecd3b.jpeg',
      ViiJKe: 'https://cdn.klaviyomail.com/company/YupLFH/images/e65992c9-53ef-456f-94ce-63edb6455ee4.jpeg',
      VyyvNj: 'https://cdn.klaviyomail.com/company/YupLFH/images/e65992c9-53ef-456f-94ce-63edb6455ee4.jpeg'
    },

    // Holdout test (see "Holdout test" below). It runs until the result is conclusive or for
    // 4 months, whichever is sooner. Set endsOn to 4 months after the deploy date; from then on
    // every visitor gets the popups again and orders are no longer tagged. Once the result is
    // conclusive, the shop metafield drm_exit.experiment is set to "off", which ends the test
    // the same way, with no theme change.
    experiment: {
      name: 'exit_holdout_v2',
      holdoutShare: 0.5,
      endsOn: '2027-02-23',
      gaMeasurementId: 'G-K38E46RDFD'
    }
  };

  var STORE_KEY = 'drm_exit_v1';
  var SESSION_KEY = 'drm_exit_session_v1';
  var DAY_MS = 864e5;
  var OPEN_TIMEOUT_MS = 2500;

  var debug = /[?&]drm_exit_debug=1/.test(location.search);
  function log() {
    if (debug && window.console) console.log.apply(console, ['[DRM exit]'].concat([].slice.call(arguments)));
  }

  // --- Storage ----------------------------------------------------------------

  function storage(name) {
    try { return window[name]; } catch (e) { return null; }
  }

  function load(name, key, fallback) {
    try {
      var value = JSON.parse(storage(name).getItem(key));
      return value && typeof value === 'object' ? value : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function save(name, key, value) {
    try { storage(name).setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  // Kept across visits: viewed products, Peanuts seen, when capped popups were shown.
  var saved = load('localStorage', STORE_KEY, {});
  saved.products = saved.products || [];
  saved.peanutsSeen = !!saved.peanutsSeen;
  saved.shows = saved.shows || {};

  // This visit only: products viewed, which exit popup was shown.
  var session = load('sessionStorage', SESSION_KEY, {});
  session.viewed = session.viewed || [];
  session.unavailable = session.unavailable || [];

  function persist() {
    save('localStorage', STORE_KEY, saved);
    save('sessionStorage', SESSION_KEY, session);
  }

  // --- Record this page view ---------------------------------------------------

  var page = window.DRM_EXIT_PAGE || {};

  if (page.peanuts) saved.peanutsSeen = true;

  if (page.product && page.product.handle) {
    var viewed = page.product;
    viewed.ts = Date.now();
    saved.products = [viewed]
      .concat(saved.products.filter(function (p) { return p.handle !== viewed.handle; }))
      .slice(0, 10);
    if (session.viewed.indexOf(viewed.handle) === -1) session.viewed.push(viewed.handle);
  }

  persist();

  // --- Rules -------------------------------------------------------------------

  function sessionProducts() {
    return saved.products
      .filter(function (p) { return session.viewed.indexOf(p.handle) !== -1; })
      .slice(0, CONFIG.maxProductsShown);
  }

  function shows(key) {
    return saved.shows[key] || [];
  }

  function cooledDown(key, days) {
    var last = Math.max.apply(null, [0].concat(shows(key)));
    return Date.now() - last >= days * DAY_MS;
  }

  function shippingGap(cart) {
    var threshold = CONFIG.freeShippingThresholds[cart.currency];
    return threshold ? threshold - cart.total_price : 0;
  }

  var RULES = {
    cartUnder50: function (cart) {
      return cart.item_count > 0 && shippingGap(cart) > 0;
    },
    recentlyViewed: function (cart) {
      return cart.item_count === 0 &&
        sessionProducts().length > 0 &&
        cooledDown('recentlyViewed', CONFIG.recentlyViewedCooldownDays);
    },
    noPeanuts: function () {
      return !saved.peanutsSeen &&
        shows('noPeanuts').length < CONFIG.noPeanutsMaxShows &&
        cooledDown('noPeanuts', CONFIG.noPeanutsCooldownDays);
    }
  };

  function formId(key) {
    var ids = CONFIG.forms[key];
    return ids && (window.innerWidth < CONFIG.mobileBelowWidth ? ids.mobile : ids.desktop);
  }

  function isForm(key, id) {
    var ids = CONFIG.forms[key];
    return !!ids && (ids.desktop === id || ids.mobile === id);
  }

  function storeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status + ' ' + url);
        return response.json();
      });
  }

  function getCart() {
    return getJSON(storeRoot() + 'cart.js');
  }

  // --- Popup 2: free shipping gap + frequently bought together ------------------

  var crossSell = null;
  var fbtData = null;

  function loadFbt() {
    if (fbtData) return Promise.resolve(fbtData);
    if (!page.fbtUrl) return Promise.resolve({});
    return getJSON(page.fbtUrl).then(
      function (data) { fbtData = data || {}; return fbtData; },
      function () { return {}; }
    );
  }

  // Products most often bought together with what's in the cart, best first.
  function fbtHandles(cart, data) {
    var inCart = cart.items.map(function (item) { return item.handle; });
    var scores = {};
    inCart.forEach(function (handle) {
      (data[handle] || []).forEach(function (pair) {
        if (inCart.indexOf(pair[0]) === -1) scores[pair[0]] = (scores[pair[0]] || 0) + pair[1];
      });
    });
    return Object.keys(scores).sort(function (a, b) { return scores[b] - scores[a]; });
  }

  function productDetails(handle) {
    return getJSON(storeRoot() + 'products/' + encodeURIComponent(handle) + '.js').catch(function () { return null; });
  }

  function relatedProducts(cart) {
    var url = storeRoot() + 'recommendations/products.json?limit=6&intent=related&product_id=' + cart.items[0].product_id;
    return getJSON(url).then(function (data) { return data.products || []; }, function () { return []; });
  }

  // Of the best matches, prefer one that covers the free shipping gap on its own.
  function pickProduct(products, cart, gap) {
    var inCart = cart.items.map(function (item) { return item.handle; });
    var usable = products.filter(function (p) { return p && p.available && inCart.indexOf(p.handle) === -1; });
    return usable.filter(function (p) { return p.price >= gap; })[0] || usable[0] || null;
  }

  function prepareCrossSell(cart) {
    var gap = shippingGap(cart);
    // Set first, so the amount is still filled in if the product lookup fails.
    crossSell = { amount: money(gap, cart.currency), product: null };
    return loadFbt()
      .then(function (data) {
        return Promise.all(fbtHandles(cart, data).slice(0, 6).map(productDetails));
      })
      .then(function (products) {
        return pickProduct(products, cart, gap) ||
          relatedProducts(cart).then(function (related) { return pickProduct(related, cart, gap); });
      })
      .then(function (product) {
        if (product) {
          crossSell.product = {
            title: product.title,
            url: product.url,
            image: product.featured_image ? sizedImage(product.featured_image, 500) : null,
            price: money(product.price, cart.currency)
          };
        }
        log('cross-sell', crossSell);
      });
  }

  // --- Popup 3: Peanuts fan favorites strip ------------------------------------

  var peanutsStrip = [];

  // On-model photo where there is one, otherwise the main product photo.
  function lifestyleImage(product) {
    var images = product.images || [];
    var onModel = images.filter(function (image) { return /lifest|on_model/i.test(image.src); })[0];
    var image = onModel || images[0];
    return image ? image.src : null;
  }

  // "Dancing Snoopy | Knee-High Compression Socks" -> name "Dancing Snoopy", type "Knee-High".
  function splitTitle(title) {
    var parts = title.split(' | ');
    return { name: parts[0], type: (parts[1] || '').replace(/\s*Compression Socks\s*$/i, '') };
  }

  // The store returns prices in the visitor's currency (Shopify Markets).
  function activeCurrency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  }

  // Peanuts products in stock, best sellers first (by orders in the last 12 months). Men's and
  // women's versions share a name, so each name appears once.
  function preparePeanutsStrip() {
    var url = storeRoot() + 'collections/' + CONFIG.peanutsCollection + '/products.json?limit=250';
    return Promise.all([loadFbt(), getJSON(url)]).then(function (results) {
      var bestsellers = results[0]._bestsellers || [];
      var rank = function (handle) {
        var position = bestsellers.indexOf(handle);
        return position === -1 ? bestsellers.length : position;
      };
      var names = [];
      peanutsStrip = (results[1].products || [])
        .filter(function (p) { return p.variants.some(function (v) { return v.available; }) && lifestyleImage(p); })
        .sort(function (a, b) { return rank(a.handle) - rank(b.handle); })
        .map(function (p) {
          var title = splitTitle(p.title);
          var prices = p.variants
            .filter(function (v) { return v.available; })
            .map(function (v) { return Math.round(parseFloat(v.price) * 100); });
          return {
            name: title.name, type: title.type, url: storeRoot() + 'products/' + p.handle,
            image: sizedImage(lifestyleImage(p), 300), price: money(Math.min.apply(null, prices), activeCurrency())
          };
        })
        .filter(function (p) {
          if (names.indexOf(p.name) !== -1) return false;
          names.push(p.name);
          return true;
        })
        .slice(0, CONFIG.peanutsStripCount);
      log('peanuts strip', peanutsStrip);
    });
  }

  // --- Fonts -----------------------------------------------------------------------

  // The Klaviyo forms are designed with Jost (headings) and Nunito Sans (text). On the storefront
  // those are swapped for the theme's own heading and text fonts (already loaded by the theme),
  // so the popups match the rest of the site. No extra font files are requested.
  function loadFonts() {
    addStyles();
    return Promise.resolve();
  }

  var FONT_HEADING = 'var(--heading-font-family, inherit)';
  var FONT_TEXT = 'var(--text-font-family, inherit)';

  // Re-fonts every element inside a popup that Klaviyo set in Jost or Nunito Sans.
  function applyThemeFonts(root) {
    var nodes = [root].concat([].slice.call(root.querySelectorAll('*')));
    nodes.forEach(function (node) {
      if (node.nodeType !== 1 || /^(script|style|svg|path|img)$/i.test(node.tagName)) return;
      var family = getComputedStyle(node).fontFamily || '';
      if (/jost/i.test(family)) node.style.setProperty('font-family', FONT_HEADING, 'important');
      else if (/nunito/i.test(family)) node.style.setProperty('font-family', FONT_TEXT, 'important');
    });
  }

  // Also covers cards the script adds after the popup opens (products, the Peanuts strip).
  function watchThemeFonts(layer) {
    if (!layer || layer.__drmFonts || !window.MutationObserver) return;
    layer.__drmFonts = true;
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () { applyThemeFonts(layer); }, 60);
    }).observe(layer, { childList: true, subtree: true });
  }

  // Work to finish before a popup opens, so it never appears half empty.
  var PREPARE = {
    cartUnder50: function (cart) { return Promise.all([prepareCrossSell(cart), loadFonts()]); },
    recentlyViewed: loadFonts,
    noPeanuts: function () { return Promise.all([preparePeanutsStrip(), loadFonts()]); }
  };

  // --- Getting ready before the visitor leaves ----------------------------------

  // So a popup opens the moment the visitor moves to leave, the script works out which popup
  // they would get a few seconds after each page loads (and again whenever the cart changes),
  // then fetches its products, fonts and images in advance.
  var prepared = {};

  function cartSignature(cart) {
    return cart.currency + ' ' + cart.total_price + ' ' +
      cart.items.map(function (item) { return item.handle + 'x' + item.quantity; }).join(',');
  }

  function preloadImages(urls) {
    urls.forEach(function (url) { if (url) new Image().src = url; });
  }

  function popupImages(key) {
    var urls = [CONFIG.formImages[formId(key)]];
    if (key === 'recentlyViewed') urls = urls.concat(sessionProducts().map(function (p) { return p.image; }));
    if (key === 'cartUnder50' && crossSell && crossSell.product) urls.push(crossSell.product.image);
    if (key === 'noPeanuts') urls = urls.concat(peanutsStrip.map(function (p) { return p.image; }));
    return urls;
  }

  // Each popup is prepared once per page; popup 2 again whenever the cart changes.
  function prepare(key, cart) {
    var signature = key === 'cartUnder50' ? cartSignature(cart) : key;
    if (!prepared[key] || prepared[key].signature !== signature) {
      var work = PREPARE[key](cart)
        .then(function () { preloadImages(popupImages(key)); })
        .catch(function (error) { log('prepare failed', key, error); });
      prepared[key] = { signature: signature, ready: work };
    }
    return prepared[key].ready;
  }

  function eligibleKeys(cart) {
    return CONFIG.priority.filter(function (key) {
      return formId(key) && session.unavailable.indexOf(key) === -1 && RULES[key](cart);
    });
  }

  function getReady(cart) {
    var key = !session.shown && eligibleKeys(cart)[0];
    if (!key || testGroup() === 'holdout') return;
    log('getting ready:', key);
    prepare(key, cart);
  }

  function warmUp() {
    if (session.shown) return;
    getCart().then(getReady).catch(function (error) { log('warm-up failed', error); });
  }

  setTimeout(function () {
    if (window.requestIdleCallback) requestIdleCallback(warmUp, { timeout: 2000 });
    else warmUp();
  }, CONFIG.minSecondsOnPage * 1000);

  // The theme announces cart updates (add to cart, quantity changes) along with the new cart.
  document.documentElement.addEventListener('cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && cart.items && typeof cart.total_price === 'number') getReady(cart);
    else warmUp();
  });

  // --- Klaviyo form events -----------------------------------------------------

  var openForms = [];
  var pendingOpen = null;

  window.addEventListener('klaviyoForms', function (e) {
    var detail = e.detail || {};
    if (detail.type === 'open') {
      if (openForms.indexOf(detail.formId) === -1) openForms.push(detail.formId);
      if (pendingOpen && pendingOpen.formId === detail.formId) pendingOpen.resolve(true);
      whenRendered(PANELS, function (panels) {
        panels.forEach(function (panel) {
          // Same as the :has() rule in STYLES, for browsers without :has().
          var block = panel.closest('[data-testid="form-component"]');
          if (block) block.style.setProperty('padding', '0', 'important');
          // The cart drawer (Upnova) sits at the highest z-index there is, above Klaviyo's
          // 90000. Klaviyo adds its popup last in the page, so at the same z-index it wins.
          var layer = panel.parentElement;
          while (layer && layer !== document.body && getComputedStyle(layer).position !== 'fixed') layer = layer.parentElement;
          if (layer && layer !== document.body) layer.style.setProperty('z-index', '2147483647', 'important');
          if (layer && layer !== document.body) { applyThemeFonts(layer); watchThemeFonts(layer); }
        });
      });
      if (isForm('recentlyViewed', detail.formId)) fillRecentlyViewed();
      if (isForm('cartUnder50', detail.formId)) fillShippingGap();
      if (isForm('noPeanuts', detail.formId)) fillPeanutsStrip();
    } else if (detail.type === 'close') {
      openForms = openForms.filter(function (id) { return id !== detail.formId; });
    }
  });

  function openForm(key) {
    var id = formId(key);
    return new Promise(function (resolve) {
      var timer = setTimeout(function () { pendingOpen = null; resolve(false); }, OPEN_TIMEOUT_MS);
      pendingOpen = {
        formId: id,
        resolve: function (opened) { clearTimeout(timer); pendingOpen = null; resolve(opened); }
      };
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', id]);
    });
  }

  // Tries each eligible popup in priority order until Klaviyo actually opens one.
  function openFirst(keys, cart) {
    if (!keys.length) return Promise.resolve(null);
    var key = keys[0];
    return prepare(key, cart).then(function () { return openForm(key); }).then(function (opened) {
      if (opened) {
        recordShown(key);
        log('opened', key);
        return key;
      }
      // Not live yet, or held back by Klaviyo's own settings: try the next one.
      log('did not open', key);
      session.unavailable.push(key);
      persist();
      return openFirst(keys.slice(1), cart);
    });
  }

  // One exit popup per visit; the capped popups also count toward their limits.
  function recordShown(key) {
    session.shown = key;
    saved.shows[key] = shows(key).concat(Date.now()).slice(-5);
    persist();
  }

  // --- Holdout test ----------------------------------------------------------------

  // Measures what the popups add in sales. The first time a visitor would get an exit popup,
  // they join a group at random, for good: "popup" (sees popups as normal) or "holdout" (sees
  // none). For holdout visitors the popup is recorded as if shown, so both groups follow the
  // same rules from then on. Only visitors Klaviyo will show a popup to take part (see
  // canSeePopups).
  //
  // The group goes on the visitor's cart as hidden attributes, which Shopify copies onto the
  // order, so revenue can be compared by group. The cart is tagged again on later visits,
  // because a new cart starts empty after each order. Each visitor is also reported once to
  // Google Analytics (event exit_test2_<group>_<popup>), which gives the size of each group.
  //
  // v1 (Oct 22, 2026) also enrolled visitors who hadn't accepted cookies: Klaviyo's script loads
  // for them, but it shows no popups, so v2 checks what Klaviyo itself checks.

  var POPUP_NAMES = { cartUnder50: 'free_shipping', recentlyViewed: 'recently_viewed', noPeanuts: 'peanuts' };

  function testRunning() {
    var test = CONFIG.experiment;
    return !!test && page.experiment !== 'off' && new Date() < new Date(test.endsOn + 'T00:00:00');
  }

  // The visitor's group in the current test, or null.
  function testGroup() {
    var test = saved.test;
    return testRunning() && test && test.name === CONFIG.experiment.name ? test.group : null;
  }

  // Klaviyo only starts (and so only shows popups) once Shopify's privacy API says the visitor
  // can be tracked, i.e. they accepted cookies or live where no consent is needed.
  function canSeePopups() {
    var privacy = window.Shopify && Shopify.customerPrivacy;
    var klaviyo = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"][src*="/klaviyo.js"]');
    try {
      return !!klaviyo && !!privacy && typeof privacy.userCanBeTracked === 'function' && privacy.userCanBeTracked() === true;
    } catch (e) {
      return false;
    }
  }

  function joinTestGroup(group, key) {
    saved.test = { name: CONFIG.experiment.name, group: group, popup: POPUP_NAMES[key] || 'none', at: Date.now() };
    persist();
    log('holdout test: joined group', group, 'at', saved.test.popup);
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'exit_test2_' + group + '_' + saved.test.popup, {
        send_to: CONFIG.experiment.gaMeasurementId,
        exit_test: saved.test.name
      });
    }
  }

  // Called when the visitor is about to get popup `key`. Returns their group, or null.
  function enroll(key) {
    if (!testRunning()) return null;
    if (!testGroup()) {
      if (!canSeePopups()) return null;
      joinTestGroup(Math.random() < CONFIG.experiment.holdoutShare ? 'holdout' : 'popup', key);
    }
    return testGroup();
  }

  function tagCart(cart) {
    if (!testGroup()) return;
    var attributes = { _drm_exit_test: saved.test.name, _drm_exit_group: saved.test.group, _drm_exit_popup: saved.test.popup };
    var current = cart.attributes || {};
    var tagged = Object.keys(attributes).every(function (name) { return current[name] === attributes[name]; });
    if (tagged) return;
    fetch(storeRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(
      function () { log('holdout test: cart tagged', attributes); },
      function (error) { log('holdout test: cart tag failed', error); }
    );
  }

  if (testGroup()) getCart().then(tagCart).catch(function () {});

  // --- Exit intent ---------------------------------------------------------------

  var pageLoadedAt = Date.now();
  var checking = false;

  function onExitIntent(source) {
    if (checking || session.shown) return;
    if (Date.now() - pageLoadedAt < CONFIG.minSecondsOnPage * 1000) return;
    if (openForms.length) return; // another Klaviyo popup is already on screen

    checking = true;
    getCart()
      .then(function (cart) {
        var eligible = eligibleKeys(cart);
        log('exit intent via', source, '| cart', cart.item_count, cart.total_price, '| eligible', eligible);
        if (!eligible.length) return null;
        var group = enroll(eligible[0]);
        if (group) tagCart(cart);
        if (group === 'holdout') {
          recordShown(eligible[0]);
          log('holdout test: held back', eligible[0]);
          return null;
        }
        return openFirst(eligible, cart);
      })
      .catch(function (error) { log('error', error); })
      .then(function () { checking = false; });
  }

  // Desktop: the cursor leaves through the top of the window. Some browsers report a fast exit
  // a few pixels inside the window, hence the small margin.
  document.addEventListener('mouseout', function (e) {
    if (!e.relatedTarget && e.clientY < CONFIG.exitTopMarginPx) onExitIntent('mouse');
  });

  // Touch devices: a fast scroll back up after reading down the page.
  if (window.matchMedia && matchMedia('(hover: none) and (pointer: coarse)').matches) {
    var lastY = window.scrollY;
    var lastTime = Date.now();
    var deepestY = lastY;

    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      var now = Date.now();
      var speed = (lastY - y) / Math.max(now - lastTime, 1); // px per ms, upwards
      deepestY = Math.max(deepestY, y);
      if (speed > 1.5 && deepestY > 400 && y < deepestY - 200) onExitIntent('fast scroll up');
      lastY = y;
      lastTime = now;
    }, { passive: true });
  }

  // --- Popup content -------------------------------------------------------------

  // The popups are fully styled inside Klaviyo (separate desktop and mobile forms); these
  // remove the padding Klaviyo puts around every custom HTML block (10px 6px, which would
  // inset each design and narrow it by 12px; Klaviyo has no setting for it), and add hover
  // effects and popup 3's scrolling (also in that form's custom CSS, so the Klaviyo editor
  // preview scrolls). The strip's animation is inline, hence the !important.
  var PANELS = '.drm-rv2, .drm-fs2, .drm-pn3';
  var STYLES =
    '[data-testid="form-component"]:has(> * > .drm-rv2, > * > .drm-fs2, > * > .drm-pn3){padding:0 !important}' +
    '.drm-card{transition:transform .2s,box-shadow .2s}' +
    '.drm-card:hover{transform:translateY(-2px);box-shadow:0 18px 40px rgba(70,30,20,.24) !important}' +
    '.drm-cta:hover{filter:brightness(1.12)}' +
    '.drm-ghost{transition:background .2s,color .2s}' +
    '.drm-ghost:hover{background:#1A1A1A !important;color:#fff !important}' +
    '.drm-dismiss:hover{color:#000 !important}' +
    '@keyframes drm-pn-scroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}' +
    '.drm-pn--desktop:hover .drm-pn__track,.drm-pn--mobile:active .drm-pn__track,' +
    '.drm-pn:focus-within .drm-pn__track{animation-play-state:paused !important}' +
    '.drm-pn__card{transition:transform .2s,box-shadow .2s}' +
    '.drm-pn--desktop .drm-pn__card:hover{transform:translateY(-3px);box-shadow:0 14px 28px rgba(70,30,20,.22) !important}' +
    '.drm-pn-cta:hover{filter:brightness(1.12)}' +
    '.klaviyo-form [style*="Jost" i]{font-family:var(--heading-font-family,inherit) !important}' +
    '.klaviyo-form [style*="Nunito" i]{font-family:var(--text-font-family,inherit) !important}' +
    '@media (prefers-reduced-motion:reduce){.drm-pn{overflow-x:auto !important}.drm-pn__track{animation:none !important}}';

  function addStyles() {
    if (document.getElementById('drm-exit-styles')) return;
    var style = document.createElement('style');
    style.id = 'drm-exit-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  // Klaviyo renders the form just after its "open" event, so wait for its blocks. The desktop
  // and mobile blocks may both be in the page, so every match is filled.
  function whenRendered(selector, callback) {
    var attempts = 0;
    (function attempt() {
      var found = document.querySelectorAll(selector);
      if (found.length) return callback([].slice.call(found));
      if (attempts++ < 40) setTimeout(attempt, 50);
    })();
  }

  function field(root, name) {
    return [].slice.call(root.querySelectorAll('[data-drm-field="' + name + '"]'));
  }

  // Puts a product into one of the sample cards from the Klaviyo HTML.
  function fillCard(card, product) {
    field(card, 'link').forEach(function (link) { link.href = product.url; });
    field(card, 'image').forEach(function (img) {
      if (product.image) img.src = product.image;
      img.alt = product.title;
    });
    field(card, 'title').forEach(function (node) { node.textContent = product.title; });
    field(card, 'price').forEach(function (node) { node.textContent = product.price; });
  }

  function fillRecentlyViewed() {
    whenRendered('[data-drm-slot="recently-viewed"]', function (slots) {
      addStyles();
      var products = sessionProducts();
      slots.forEach(function (slot) {
        var cards = [].slice.call(slot.querySelectorAll('[data-drm-card]'));
        cards.forEach(function (card, i) {
          if (products[i]) fillCard(card, products[i]);
          else card.remove();
        });
        if (products.length === 1) {
          slot.style.gridTemplateColumns = 'minmax(0, 300px)';
          slot.style.justifyContent = 'center';
        }
      });
    });
  }

  // "$20.00"; other currencies get their code appended ("$85.00 CAD").
  function money(cents, currency) {
    var text = '$' + (cents / 100).toFixed(2);
    return currency && currency !== 'USD' ? text + ' ' + currency : text;
  }

  function sizedImage(src, width) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function fillShippingGap() {
    whenRendered('[data-drm-slot="cart-xsell"]', function (slots) {
      addStyles();
      if (!crossSell) return;
      field(document, 'gap-amount').forEach(function (node) { node.textContent = crossSell.amount; });
      slots.forEach(function (slot) {
        var card = slot.querySelector('[data-drm-card]');
        if (crossSell.product && card) fillCard(card, crossSell.product);
        else slot.style.display = 'none';
      });
    });
  }

  // One strip card, copied from the sample card in the Klaviyo HTML. The second copy of the
  // list is hidden from screen readers and keyboard focus.
  function peanutsCard(template, product, hidden) {
    var card = template.cloneNode(true);
    card.href = product.url;
    if (hidden) {
      card.setAttribute('aria-hidden', 'true');
      card.tabIndex = -1;
    } else {
      card.removeAttribute('aria-hidden');
      card.removeAttribute('tabindex');
    }
    field(card, 'image').forEach(function (img) {
      img.src = product.image;
      img.alt = hidden ? '' : product.name;
    });
    field(card, 'name').forEach(function (node) { node.textContent = product.name; });
    field(card, 'type').forEach(function (node) { node.textContent = product.type; });
    field(card, 'price').forEach(function (node) { node.textContent = product.price; });
    return card;
  }

  // The list is drawn twice and slid left by half its width, so the loop is seamless. The
  // duration scales with the number of products so the speed matches the design.
  function fillPeanutsStrip() {
    whenRendered('[data-drm-slot="peanuts-strip"]', function (tracks) {
      addStyles();
      if (!peanutsStrip.length) return;
      tracks.forEach(function (track) {
        var template = track.querySelector('[data-drm-card]');
        if (!template) return;
        var cards = peanutsStrip.map(function (p) { return peanutsCard(template, p, false); })
          .concat(peanutsStrip.map(function (p) { return peanutsCard(template, p, true); }));
        var perItem = parseFloat(track.getAttribute('data-drm-seconds-per-item')) || 6.5;
        track.innerHTML = '';
        cards.forEach(function (card) { track.appendChild(card); });
        track.style.animationDuration = (peanutsStrip.length * perItem) + 's';
      });
    });
  }

  // Links marked data-drm-close (popup 1's "No thanks", popup 2's "Continue shopping") close the
  // popup through Klaviyo's own close button.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-drm-close]');
    if (!trigger) return;
    e.preventDefault();
    var dialog = trigger.closest('[role="dialog"]') || document;
    var close = dialog.querySelector('.klaviyo-close-form, button[aria-label="Close dialog"], button[aria-label*="Close"]');
    if (close) close.click();
  });

  // Popup 3's "Explore Peanuts" button or a product card: the visitor is on their way to Peanuts,
  // so popup 3 never shows again. Saved at the click, before the page changes.
  document.addEventListener('click', function (e) {
    if (!(e.target.closest && e.target.closest('.drm-pn3 a[href]'))) return;
    saved.peanutsSeen = true;
    persist();
    log('peanuts link clicked; popup 3 retired');
  });

  // --- QA helpers ----------------------------------------------------------------

  window.DRMExitPopups = {
    config: CONFIG,
    state: function () {
      return { saved: saved, session: session, sessionProducts: sessionProducts(), prepared: Object.keys(prepared), crossSell: crossSell, peanutsStrip: peanutsStrip, openForms: openForms, testRunning: testRunning(), testGroup: testGroup() };
    },
    // QA only: orders from these carts are tagged with popup "qa", and nothing is sent to Google
    // Analytics, so test visits stay out of the results.
    joinTestGroup: function (group) {
      if (group !== 'popup' && group !== 'holdout') return 'use "popup" or "holdout"';
      saved.test = { name: CONFIG.experiment.name, group: group, popup: 'qa', at: Date.now() };
      persist();
      log('holdout test: QA visitor put in group', group);
      return testGroup();
    },
    trigger: function () {
      debug = true;
      pageLoadedAt = 0;
      onExitIntent('manual');
    },
    reset: function () {
      try { storage('localStorage').removeItem(STORE_KEY); } catch (e) {}
      try { storage('sessionStorage').removeItem(SESSION_KEY); } catch (e) {}
      log('stored state cleared; reload the page');
    }
  };
})();
